import {isArray, isNull, isObject} from './typing.helpers';
import {capitalize} from '../transformation.helpers';
import {ComponentType} from '@angular/cdk/portal';
import {isPaginationInfo, PaginationInfo} from '../pagination/pagination.types';
import {MxSelectorOrSimply} from '@aviary/store/store.types';

export const COMMON_FEATURE_KEY = 'common';

export type LoadStatus = 'PRELOAD' | 'LOADING' | 'RELOADING' | 'LOADED' | 'FAILED';
export const PRELOAD = 'PRELOAD' as const;
export const LOADING = 'LOADING' as const;
export const RELOADING = 'RELOADING' as const;
export const LOADED = 'LOADED' as const;
export const FAILED = 'FAILED' as const;

export interface ListData<T> {
  readonly data: T[];
  readonly filterData: ListFilterData;
  readonly paginationInfo?: PaginationInfo;
}

export function isListData(thing: unknown): thing is ListData<unknown> {
  return isObject(thing) && (isArray(thing['data']) || isNull(thing['data'])) && isPaginationInfo(thing['paginationInfo']);
}

export const EMPTY_LIST_DATA: ListData<never> = {
  data: [],
  filterData: {}
};

export interface ListFilterData {
  readonly [filterName: string]: ListFilterOptions;
}

export type ListFilterOptions = ListFilterOption[];

export interface ListFilterOption {
  displayValue: string;
  filterValue: string;
  count?: number;
}

export interface SubstateWithListFilterData {
  filterData: ListFilterData;
}

export type ListFilterDisplayNameOverridesMapperFunction = (displayName: string) => string;

export type ListFilterDisplayNameOverridesMap = Map<string, string>;

export type ListFilterDisplayNameOverrides = MxSelectorOrSimply<ListFilterDisplayNameOverridesMap> | ListFilterDisplayNameOverridesMapperFunction;

export type ListFilterSelectOptions = ListFilterSelectOption[];

export interface ListFilterSelectOption extends SelectOption<string> {
  count: number | undefined;  // NOT count?: number -- because we want type differentiation between this and SelectOption.
}

export interface FacetExtractor {
  addProperty: string,
  fromProperty: string
  facetName: string,
}

export type FacetExtractors = FacetExtractor[];

export interface AddressType {
  readonly id: number;
  readonly description: string;
}

export interface Address {
  readonly address: string;
  readonly address2: string;
  readonly city: string;
  readonly state: State;
  readonly zipCode: string;
  readonly latitude: number;
  readonly longitude: number;
}

export type SimpleAddress = Omit<Address, 'latitude' | 'longitude'>;
export type SimpleAddressWithCountry = SimpleAddress & { country: string; };

export interface Contact {
  readonly name: string;
  readonly title: string;
  readonly phone: string;
  readonly email: string;
}

export type PhoneType = 'HOME' | 'MOBILE' | 'WORK';

export const PHONE_TYPE_SELECT_OPTIONS: SelectOptions<PhoneType> = [
  {displayName: 'Home', value: 'HOME'},
  {displayName: 'Cell', value: 'MOBILE'},
  {displayName: 'Work', value: 'WORK'}
];

export interface Pronoun {
  readonly id: number;
  readonly pronounsString: string;
}

export interface MaterialType {
  readonly code: MaterialTypeCode;
  readonly name: string;
  readonly description: string;
}

export enum MaterialTypeCode {
  // Print & special formats
  ARCHIVAL = 'mix',
  ATLAS = 'atl',
  BOOK = 'bks',
  LARGE_PRINT = 'lpt',
  BRAILLE = 'brl',
  NEWSPAPER = 'new',
  PERIODICAL = 'per',
  SERIAL = 'ser',
  PRINTED_MUSIC = 'pmu',

  // Audio
  AUDIO_BOOK = 'abk',
  AUDIOBOOK_ON_CASSETTE = 'abt', // custom svg
  AUDIO_ON_CD = 'abc', // custom svg
  BOOK_ON_CD = 'bcd',
  BOOK_ON_CASSETTE = 'bcs',
  MUSIC = 'mus',
  MUSIC_CD = 'mcd',
  MUSICAL_SOUND_RECORDING = 'msr',

  // Video
  DVD = 'dvd',
  // DVD_OCLC = 'dvv', // Do we need OCLC icons?
  BLU_RAY = 'brd',
  BLU_RAY_DVD = 'bdv',
  VIDEO_TAPE = 'vcr',
  VIDEO_RECORDING = 'vid',
  VIDEO_GAME = 'vgm',

  // Electronic
  EBOOK = 'ebk',
  E_AUDIOBOOK = 'aeb',
  E_RESOURCE = 'elr',

  // Other physical media
  KIT = 'kit',
  PROJECTED_MEDIUM = 'pgr',

  // Fallback
  UNKNOWN = 'ukn',
}

export type CoverImageSource = 'CHILIFRESH' | 'BOWKER';
export const PRIMARY_COVER_IMAGE_SOURCE: CoverImageSource = 'CHILIFRESH';

/*
TODO: Dates come from the API as strings.  We can keep them typed as strings in the store, and let the Date pipe format them at the component template.
(Or if we need to do any calculations on dates, we can convert them at that point.)

So for now, these are a couple of documenting types to keep them straight.

For the longer term, Zod looks promising for specific string subtypes:  https://github.com/colinhacks/zod
See also: https://www.youtube.com/watch?v=QChERRgDtoM
*/
/** TEMPORARY DOCUMENTING TYPE!  Just a string for now.  See TODO in common.types.ts. */
export type DateTimeIsoString = string;

/** TEMPORARY DOCUMENTING TYPE!  Just a string for now.  See TODO in common.types.ts. */
export type DateTimeIsoWithMillisecondsString = string;

/** TEMPORARY DOCUMENTING TYPE!  Just a string for now.  See TODO in common.types.ts. */
export type DateOnlyIsoString = string;

/** TEMPORARY DOCUMENTING TYPE!  Just a string for now.  See TODO in common.types.ts. */
export type DateOnlyRangeIsoString = string;

export type DateOnlyRangeIsoObject = {
  start: DateOnlyIsoString;
  end: DateOnlyIsoString;
};

export type DateRangeObject = {
  start?: Date;
  end?: Date;
}

export type DateString = DateTimeIsoString | DateOnlyIsoString;

export type TimeOfDay = 'START_OF_DAY' | 'END_OF_DAY' | 'NEITHER';

export type StatusChipColor = 'status-positive' | 'status-neutral' | 'status-negative' | 'status-transitional' | 'status-inactive';

export type StatusChipColors<T> = {
  readonly [color in StatusChipColor]: T[];
};

export type ActivationStatusColor = 'activation-status-active' | 'activation-status-deactivated';

export const NO_OP = () => {
  // This function is empty because no-op functions don't do anything, by definition. (This comment prevents a SonarLint complaint.)
};

export type AddOrEdit = 'ADD' | 'EDIT';
export type ActionContext = 'DETAILS' | 'LIST' | 'PARENT';
export const NOTE_TYPES = ['INFORMATIONAL', 'ALERT'] as const;
export type NoteType = typeof NOTE_TYPES[number];
export const CIRCULATION_ITEM_BLOCK_CODES = [
  'LIBRARY_DEFINED_CIRCULATION_ITEM', 'FREE_TEXT_CIRCULATION_ITEM', 'ITEM_NOT_CHECKED_OUT'
] as const;
export type CirculationItemBlockCode = typeof CIRCULATION_ITEM_BLOCK_CODES[number];

export const NOTE_TYPE_SELECT_OPTIONS: SelectOptions<NoteType> =
  NOTE_TYPES.map(type => ({displayName: capitalize(String(type).toLowerCase()), value: type}));

export type SelectOptions<ValueType> = SelectOption<ValueType>[];

export interface SelectOption<ValueType> {
  displayName: string;
  value: ValueType;
}

export interface DisabledSelectOption {
  displayName: string;
  tooltip?: string;
}

export type DisabledSelectOptions = DisabledSelectOption[];

export type ButtonSelectOptions<ValueType> = ButtonSelectOption<ValueType>[];

export interface ButtonSelectOption<ValueType> extends SelectOption<ValueType> {
  buttonColor: string; // Should be ThemePalette, actually - but we need to make our own type for that, so that we can have more than three colors.
}

export type ChipSelectOptions<ValueType> = ChipSelectOption<ValueType>[];

export interface ChipSelectOption<ValueType> extends SelectOption<ValueType> {
  chipColor: string; // Should be ThemePalette, actually - but we need to make our own type for that, so that we can have more than three colors.
}

const US_STATES_AND_TERRITORIES = [
  'AE', 'AA', 'AL', 'AK', 'AS', 'AZ', 'AP', 'AR', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL', 'GA', 'GU', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS'
  , 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA'
  , 'PR', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'VI'
] as const;

type UsStateOrTerritory = typeof US_STATES_AND_TERRITORIES[number];
type CanadianProvince = 'ON' | 'QC' | 'NS' | 'NB' | 'MB' | 'BC' | 'PE' | 'SK' | 'AB' | 'NL';
export type State = UsStateOrTerritory | CanadianProvince | '';

export const STATE_SELECT: SelectOptions<string> =
  US_STATES_AND_TERRITORIES.map(value => ({
    displayName: value, value
  }));

export type AnyComponent = ComponentType<any>;  // eslint-disable-line @typescript-eslint/no-explicit-any -- This is literally any component type.
export type ComponentInputValues = Record<string, any>;   // eslint-disable-line @typescript-eslint/no-explicit-any -- Component inputs can be of any type.

export const MX_STANDARD_ANIMATION_DURATION_MILLISECONDS: number = 225;
export const MX_STANDARD_ANIMATION_TIMINGS: string = `${MX_STANDARD_ANIMATION_DURATION_MILLISECONDS}ms ease-in-out`;

export const DEFAULT_EMPTY_VALUE_REPLACEMENT_STRING: string = '--';

export type ApiErrorCode =
  'ACTION_BLOCKED'
  | 'BRANCH_INVALID_ID'
  | 'CATALOG_RECORD_INVALID_ID'
  | 'CIRCULATION_EXISTING_CHECKOUT'
  | 'CIRCULATION_INVALID_SESSION_ID'
  | 'CIRCULATION_INVALID_ITEM_ID'
  | 'CIRCULATION_INVALID_STATUS_FOR_ACTION'
  | 'CIRCULATION_NO_VALID_ITEM'
  | 'CIRCULATION_INVALID_SHELF_ID'
  | 'CIRCULATION_INVALID_COLLECTION_ID'
  | 'CIRCULATION_NO_ACTIVE_CHECKOUT'
  | 'CHECKOUT_INVALID_ID'
  | 'CHECKOUT_INVALID_DUE_DATE'
  | 'CHECKOUT_INVALID_STATUS'
  | 'CHECKOUT_RENEW_BLOCKED_CIRC_RULE'
  | 'CHECKOUT_RENEW_BLOCKED_HOLD'
  | 'ENTITY_ID_NOT_FOUND'
  | 'ENTITY_USED_AS_REFERENCE'
  | 'FRAMEWORK_ERROR'
  | 'GENERAL_ABBREVIATION_IN_USE'
  | 'GENERAL_MISSING_PARAM'
  | 'GENERAL_INVALID_VALUE'
  | 'GENERAL_BARCODE_IN_USE'
  | 'GENERAL_DUPLICATE_EMAIL'
  | 'HOLD_INVALID_ID'
  | 'HOLD_INVALID_STATUS_FOR_ACTION'
  | 'HOLD_INVALID_PAUSE_DATE'
  | 'HOLD_ITEM_BIB_MISMATCH'
  | 'HOLD_NO_VALID'
  | 'HOLDS_PRESENT_ON_ITEM'
  | 'MONTHLY_ITEM_REQUEST_LIMIT_EXCEEDED'
  | 'ORG_MISMATCH'
  | 'PATRON_HOLDS_AVAILABLE'
  | 'PATRON_INVALID_ID'
  | 'PATRON_INVALID_STATUS_FOR_ACTION'
  | 'PATRON_INVALID_STANDING_FOR_ACTION'
  | 'PATRON_GUARDIAN_REQUIRED'
  | 'PATRON_CUSTOM_FIELD_REQUIRED'
  | 'PATRON_CURRENT_PIN_INCORRECT'
  | 'PATRON_PIN_CHANGE_LOCKED'
  | 'PATRON_VERIFICATION_CODE_INCORRECT'
  | 'PATRON_VERIFICATION_EXPIRED'
  | 'PATRON_VERIFICATION_INVALID_STATUS'
  | 'PATRON_VERIFICATION_INVALID_TYPE'
  | 'REQUEST_INVALID_STATUS'
  | 'USER_INVALID_ID'
  | 'USER_NOT_OWNER';

export const DEFAULT_LIBRARY_ICON_SVG_DATA: string = `
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g id="local_library">
      <path id="Vector"
            d="M12 9.22461C14.21 9.22461 16 7.43461 16 5.22461C16 3.01461 14.21 1.22461 12 1.22461C9.79 1.22461 8 3.01461 8 5.22461C8 7.43461 9.79 9.22461 12 9.22461ZM12 3.22461C13.1 3.22461 14 4.12461 14 5.22461C14 6.32461 13.1 7.22461 12 7.22461C10.9 7.22461 10 6.32461 10 5.22461C10 4.12461 10.9 3.22461 12 3.22461ZM12 11.7746C9.64 9.57461 6.48 8.22461 3 8.22461V19.2246C6.48 19.2246 9.64 20.5746 12 22.7746C14.36 20.5846 17.52 19.2246 21 19.2246V8.22461C17.52 8.22461 14.36 9.57461 12 11.7746ZM19 17.3546C16.47 17.6946 14.07 18.6546 12 20.1746C9.94 18.6546 7.53 17.6846 5 17.3446V10.3946C7.1 10.7746 9.05 11.7446 10.64 13.2246L12 14.5046L13.36 13.2346C14.95 11.7546 16.9 10.7846 19 10.4046V17.3546Z"
            fill="#1660A5"/>
    </g>
  </svg>
`;

export interface UserInfo {
  readonly id: number;
  readonly organizationId: number;
  readonly firstName: string;
  readonly lastName: string;
}

export type NumericRange = [number, number];
